/**
 * DigestHelpContent Component
 *
 * Contains comprehensive documentation for the Monitoring Digest page.
 * Explains aggregated metrics and how to interpret digest reports.
 */
function DigestHelpContent() {
  return (
    <div className="digest-help-content">
      <section className="mb-4">
        <h6 className="fw-bold">Overview</h6>
        <p>
          The Monitoring Digest provides aggregated health and performance data
          over a selected time period. This view is useful for understanding
          trends, identifying patterns, and generating periodic health reports.
        </p>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Time Period Selection</h6>
        <p>Select from pre-defined time periods to view aggregated metrics:</p>
        <div className="ms-3">
          <p className="mb-2">
            <strong>Last Hour:</strong> Recent performance snapshot for
            immediate troubleshooting.
          </p>
          <p className="mb-2">
            <strong>Last 24 Hours:</strong> Daily health summary, useful for
            daily operational reviews.
          </p>
          <p className="mb-2">
            <strong>Last 7 Days:</strong> Weekly trends and patterns. Good for
            identifying recurring issues.
          </p>
          <p className="mb-2">
            <strong>Last 30 Days:</strong> Monthly overview for reporting and
            capacity planning.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Summary Statistics</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>Total Incidents:</strong> Number of incidents detected
            during the selected period, regardless of severity.
          </p>
          <p className="mb-2">
            <strong>Critical Incidents:</strong> Number of critical-severity
            incidents requiring immediate attention.
          </p>
          <p className="mb-2">
            <strong>Warnings:</strong> Number of warning-level incidents
            indicating metrics approaching thresholds.
          </p>
          <p className="mb-2">
            <strong>Avg Uptime:</strong> Average system uptime percentage over
            the selected period. SLA target is 99.9%.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">SLA Performance Metrics</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>Average Response Time:</strong> Mean API response time
            across all requests in the period. Target: &lt;500ms.
          </p>
          <p className="mb-2">
            <strong>Error Rate:</strong> Percentage of failed requests over
            total requests. Target: &lt;1%.
          </p>
          <p className="mb-2">
            <strong>Total Requests:</strong> Total number of API requests
            processed during the period.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Performance Metrics</h6>
        <div className="ms-3">
          <p className="mb-2">
            <strong>P50 Latency:</strong> Median response time. 50% of requests
            completed faster than this value.
          </p>
          <p className="mb-2">
            <strong>P95 Latency:</strong> 95th percentile response time. Only 5%
            of requests were slower than this.
          </p>
          <p className="mb-2">
            <strong>P99 Latency:</strong> 99th percentile response time.
            Indicates worst-case performance for most users.
          </p>
          <p className="mb-2">
            <strong>DB Pool Usage:</strong> Average database connection pool
            usage percentage. Warning threshold: &gt;70%.
          </p>
        </div>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Interpreting the Digest</h6>
        <p>Use the digest report to:</p>
        <ul>
          <li>
            Verify SLA compliance by comparing uptime and response times against
            targets
          </li>
          <li>Identify trends in incident frequency and severity over time</li>
          <li>
            Spot patterns in performance degradation (e.g., specific times of
            day)
          </li>
          <li>Generate reports for stakeholders and operational reviews</li>
          <li>Plan capacity upgrades based on resource utilization trends</li>
        </ul>
      </section>

      <section className="mb-4">
        <h6 className="fw-bold">Data Collection</h6>
        <p>
          Digest data is aggregated from metrics collected in 5-minute
          intervals. Longer time periods provide more comprehensive trend
          analysis but may mask short-term spikes or issues.
        </p>
      </section>

      <section>
        <h6 className="fw-bold">Best Practices</h6>
        <ul>
          <li>
            Review the 24-hour digest daily as part of operational routines
          </li>
          <li>
            Use the 7-day digest for weekly team reviews and retrospectives
          </li>
          <li>
            Generate 30-day digests for monthly reports and trend analysis
          </li>
          <li>
            Compare digest metrics over multiple periods to identify improvement
            or degradation trends
          </li>
          <li>
            Investigate any upward trends in error rates or response times
            before they become critical
          </li>
        </ul>
      </section>
    </div>
  )
}

export default DigestHelpContent
